import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../components/LanguageContext';
import { translations } from '../translations';
import { ChevronDown } from 'lucide-react';
export function Services() {
  const {
    language
  } = useLanguage();
  const t = translations.services[language];
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <AnimatePresence mode="wait" data-id="element-245" data-mp="2cvo0xozgkwhn">
      <motion.div key={language} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="min-h-screen bg-dark pt-32 pb-24 px-6 md:px-12" style={{
      paddingBottom: "414px"
    }} data-id="element-246" data-mp="27wkv42bnmm8m">
        <div className="max-w-5xl mx-auto" data-id="element-247" data-mp="1bop9knwf3ebc">
          {/* Header */}
          <div className="text-center mb-20" data-id="element-248" data-mp="1fozgc2s1a8r9">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="text-4xl md:text-5xl font-cinzel text-cream mb-6" data-id="element-249" data-mp="1fo7uhjo8npe6">
              {t.pageTitle}
            </motion.h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 1,
            delay: 0.3
          }} className="h-[1px] w-24 bg-gold mx-auto origin-center" data-id="element-250" data-mp="1ucu7vfub272r" />
          </div>

          {/* Grid */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-251" data-mp="4zz4h0jsvlfw">
            {t.services.map(service => {
            const isExpanded = expandedId === service.id;
            return <motion.div key={service.id} variants={cardVariants} className="bg-white/5 backdrop-blur-sm border-[0.5px] border-white/10 rounded-xl p-8 flex flex-col h-full transition-colors hover:border-gold/30" data-id="element-252" data-mp="1ccrkyfao0kup">
                  <div className="flex justify-between items-baseline mb-2" data-id="element-253" data-mp="1ldzgiplsr7l3">
                    <h3 className="text-2xl font-cinzel text-gold" data-id="element-254" data-mp="1ha9vga4bz84t">
                      {service.title}
                    </h3>
                    <span className="text-lg font-cinzel text-cream/80 ml-4" data-id="element-255" data-mp="1v8rfnxv122hz">
                      {service.price}
                    </span>
                  </div>

                  <p className="text-rose font-inter text-xs uppercase tracking-widest mb-6" data-id="element-256" data-mp="24zcada7kymvm">
                    {service.duration}
                  </p>

                  <div className="flex-grow mb-8" data-id="element-257" data-mp="2e12ar5jfww5j">
                    <motion.div initial={false} animate={{
                  height: isExpanded ? 'auto' : '44px'
                }} className="overflow-hidden" data-id="element-258" data-mp="2e1v6zckrw39f">
                      <p className={`text-cream/60 font-inter text-sm leading-relaxed whitespace-pre-line ${!isExpanded ? 'line-clamp-2' : ''}`} data-id="element-259" data-mp="2snivtb1x7vym">
                        {service.desc}
                      </p>
                    </motion.div>

                    <button onClick={() => setExpandedId(isExpanded ? null : service.id)} className="mt-4 flex items-center gap-2 text-xs font-inter uppercase tracking-widest text-gold/80 hover:text-gold transition-colors" data-id="element-260" data-mp="2jmt0e8gko7lf">
                      {isExpanded ? 'Show Less' : t.learnMore}
                      <motion.div animate={{
                    rotate: isExpanded ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-261" data-mp="2ep1atl3egxke">
                        <ChevronDown className="w-4 h-4" data-id="element-262" data-mp="24xwxeptdhkro" />
                      </motion.div>
                    </button>
                  </div>

                  <div className="mt-auto pt-6 border-t border-white/10" data-id="element-263" data-mp="1qzgqaxrlls90">
                    <Link to="/contact" className="block w-full" data-id="element-264" data-mp="1vwqfqdezm3wu">
                      <button className="w-full text-xs font-inter uppercase tracking-widest text-cream border-[0.5px] border-white/20 px-6 py-3 rounded-full hover:bg-white/10 transition-colors" data-id="element-265" data-mp="1nwsqrdkct7qp">
                        Book Session
                      </button>
                    </Link>
                  </div>
                </motion.div>;
          })}
          </motion.div>

          {/* Free Consultation CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="flex flex-col items-center py-24 md:py-32" style={{
          paddingTop: "22px",
          gap: "44px",
          paddingBottom: "0px"
        }} data-id="element-266" data-mp="157422273injs">
            <p className="max-w-md text-center font-inter text-[13px] md:text-[14px] leading-relaxed text-gold/60 mb-8" style={{
            fontSize: "18px",
            paddingTop: "0px"
          }} data-id="element-267" data-mp="2pm617tzuyp90">
              {t.slidingScale}
            </p>
            <a href="https://wa.me/64211538500?text=Hi%20Juliette%2C%20I%20would%20like%20to%20book%20a%20free%20consultation.%20Thank%20you." target="_blank" rel="noopener noreferrer" className="btn-outline-gold" data-id="element-268" data-mp="7ca2q6p3kfx0">
              Book your free consultation 
            </a>
          </motion.div>

          {/* The Kiss — Sacred Artistic Anchor */}
          <img src="https://cdn.magicpatterns.com/uploads/48XgnvVk76UTYj7or2Qpav/62a59018-7bcb-4fe9-a3b0-6bd70ef1ff25.jpg" alt="The Kiss by Gustav Klimt — circular portrait with gold frame, showing both faces in an embrace" className="w-full h-full object-contain" style={{
          paddingTop: "64px",
          paddingRight: "75px"
        }} data-id="element-269" data-mp="3kjlauizkafmd" /><div className="w-[576px] h-[576px] md:w-[768px] md:h-[768px] max-w-full aspect-square flex items-center justify-center" style={{
          paddingBottom: "565px"
        }} data-id="element-270" data-mp="2e8jnlro8glj">
              
            <p className="mt-16 text-center font-cinzel text-gold/80 tracking-wide max-w-lg italic text-[30px] md:text-[32px]" data-id="element-271" data-mp="1dsm1a8vtzzt4">
              Le Baiser — Gustav Klimt, 1907
            <p className="font-inter text-cream/50 md:text-sm text-[18px]" data-id="element-272" data-mp="35uosp5h7a004">
                Symbolism: two beings entwined, merging into gold. A celebration
                of sacred love, intimacy, and transcendent union.
              </p></p></div><motion.section initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 1.2,
          ease: 'easeOut'
        }} className="mt-40 mb-16 py-8 flex flex-col items-center" style={{
          paddingTop: "0px",
          paddingBottom: "359px",
          paddingRight: "44px"
        }} data-id="element-273" data-mp="3ev3cg0ein71i">
            

            

            <div className="mt-10 text-center space-y-2 max-w-lg" data-id="element-274" data-mp="2w3kdc4nm4d4r">
              <p className="font-cinzel text-gold/70 text-xs md:text-sm tracking-wider" data-id="element-275" data-mp="28e98oc355ylc"></p>
              
            </div>
          </motion.section>
        </div>
      </motion.div>
    </AnimatePresence>;
}